import { useState, useEffect, useMemo } from "react";
import DOMPurify from "isomorphic-dompurify";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MessageCircle, ShoppingCart } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProductImageSwiper, ProductImage } from "@/components/catalog/ProductImageSwiper";
import ProductDetailSkeleton from "@/components/catalog/ProductDetailSkeleton";
import { formatPrice } from "@/utils/format";
import { generateWhatsAppLink } from "@/utils/whatsapp";
import { trackViewItem, trackAddToCart } from "@/utils/gtm";
import { useProduct } from "@/hooks/useProducts";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

const ProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: product, isLoading, error } = useProduct(slug || '');
  const [selectedVariation, setSelectedVariation] = useState(product?.variations?.[0]);
  const [activeImageId, setActiveImageId] = useState<string | undefined>();
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    if (product?.variations && product.variations.length > 0) {
      setSelectedVariation(product.variations[0]);
    }
  }, [product]);

  // Track view_item event when product loads
  useEffect(() => {
    if (product) {
      trackViewItem({
        id: product.id,
        name: product.name,
        price: product.variations?.[0]?.price || product.base_price,
        brand: product.brand?.name,
        category: product.category?.name,
        variant: product.variations?.[0]?.name,
      });
    }
  }, [product?.id]);

  // Build array of all unique images (main + variations with images)
  const allImages = useMemo<ProductImage[]>(() => {
    if (!product) return [];
    
    const images: ProductImage[] = [];
    
    // Add main product image
    if (product.image_url) {
      images.push({ id: 'main', src: product.image_url, alt: product.name });
    }
    
    // Add variation images (only unique ones)
    product.variations?.forEach(variation => {
      if (variation.image_url && !images.some(img => img.src === variation.image_url)) {
        images.push({ 
          id: variation.id, 
          src: variation.image_url, 
          alt: `${product.name} - ${variation.name}` 
        });
      }
    });
    
    return images;
  }, [product]);

  // Handle variation selection - navigate to its image if it has one
  const handleVariationSelect = (variation: typeof selectedVariation) => {
    setSelectedVariation(variation);
    
    // If variation has an image, navigate to it
    if (variation?.image_url) {
      const imageEntry = allImages.find(img => img.src === variation.image_url);
      if (imageEntry) {
        setActiveImageId(imageEntry.id);
      }
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    const cartItemId = selectedVariation 
      ? `${product.id}-${selectedVariation.id}` 
      : product.id;

    const itemPrice = selectedVariation?.price || product.base_price;

    addItem({
      id: cartItemId,
      productId: product.id,
      productSlug: product.slug,
      name: product.name,
      variationId: selectedVariation?.id,
      variationName: selectedVariation?.name,
      price: itemPrice,
      imageUrl: selectedVariation?.image_url || product.image_url || '',
    });

    // Track add_to_cart event
    trackAddToCart({
      id: product.id,
      name: product.name,
      price: itemPrice,
      brand: product.brand?.name,
      category: product.category?.name,
      variant: selectedVariation?.name,
      quantity: 1,
    });

    toast.success("Producto agregado al carrito");
    setIsOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Skeleton className="h-5 w-36 mb-8" />
          <ProductDetailSkeleton />
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <h1 className="text-3xl font-bold mb-4">Producto no encontrado</h1>
          <Link to="/catalogo">
            <Button variant="outline">Volver al catálogo</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const handleWhatsAppClick = () => {
    const productUrl = window.location.href;
    const link = generateWhatsAppLink(
      product.name,
      selectedVariation?.name || "Sin variación",
      productUrl
    );
    window.open(link, "_blank");
    toast.success("Redirigiendo a WhatsApp...");
  };

  // Generate SEO data with fallbacks
  const seoTitle = product.seo_title || product.name;
  const seoDescription = product.seo_description || product.short_description || 
    `Comprá ${product.name} en Gordon Store. ${product.brand?.name ? `Marca ${product.brand.name}.` : ''} Productos premium para mascotas en Costa Rica.`;
  const seoImage = product.og_image_url || product.image_url || undefined;
  const seoKeywords = product.seo_keywords || 
    `${product.name}, ${product.brand?.name || ''}, ${product.category?.name || ''}, mascotas, Costa Rica`.replace(/, ,/g, ',');

  // Build breadcrumbs for structured data
  const breadcrumbs = [
    { name: "Inicio", url: "/" },
    { name: "Catálogo", url: "/catalogo" },
    ...(product.category ? [{ name: product.category.name, url: `/catalogo?category=${product.category.slug}` }] : []),
    { name: product.name, url: `/producto/${product.slug}` },
  ];

  // Build array of all product images for JSON-LD
  const allProductImageUrls = allImages.map(img => img.src);

  // Build variations data for SEO (AggregateOffer and availability)
  const variationsForSeo = product.variations?.map(v => ({
    price: v.price,
    stock: v.stock,
    name: v.name,
  }));

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        type="product"
        product={{
          name: product.name,
          description: product.short_description || undefined,
          image: allProductImageUrls.length > 0 ? allProductImageUrls : undefined,
          price: selectedVariation?.price || product.base_price,
          brand: product.brand?.name,
          category: product.category?.name,
          sku: selectedVariation?.sku || product.id,
          condition: "NewCondition",
          variations: variationsForSeo,
        }}
        breadcrumbs={breadcrumbs}
      />
      <Header />

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back button */}
        <Link to="/catalogo" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver al catálogo
        </Link>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Product Image */}
          <ProductImageSwiper 
            images={allImages} 
            activeImageId={activeImageId}
          />

          {/* Product Info */}
          <div className="space-y-6 lg:col-span-2">
            <div>
              {product.brand && (
                <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">
                  {product.brand.name}
                </p>
              )}
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{product.name}</h1>
              <p className="text-3xl font-bold text-primary">
                {formatPrice(selectedVariation?.price || product.base_price)}
              </p>
            </div>

            {product.description && (
              <div 
                className="prose prose-sm max-w-none text-muted-foreground"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(product.description) 
                }} 
              />
            )}

            {/* Variations */}
            {product.variations && product.variations.length > 0 && (
              <div className="space-y-3">
                <label className="text-sm font-semibold">Seleccioná una opción:</label>
                <div className="flex flex-wrap gap-3">
                  {product.variations.map((variation) => (
                    <Button
                      key={variation.id}
                      variant={selectedVariation?.id === variation.id ? "default" : "outline"}
                      onClick={() => handleVariationSelect(variation)}
                    >
                      {variation.name}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="pt-6 space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="flex-1 sm:flex-none min-h-[56px] py-4 sm:py-3 sm:min-h-0"
                  onClick={handleAddToCart}
                >
                  <ShoppingCart className="mr-2 h-5 w-5" />
                  Agregar al Carrito
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="flex-1 sm:flex-none min-h-[56px] py-4 sm:py-3 sm:min-h-0"
                  onClick={handleWhatsAppClick}
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Consultar por WhatsApp
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                ¿Tenés dudas? Contactanos directamente por WhatsApp
              </p>
            </div>

            {/* Additional Info */}
            <div className="pt-6 border-t border-border space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                <p className="text-muted-foreground">Envío a todo el país</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                <p className="text-muted-foreground">Productos de calidad garantizada</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                <p className="text-muted-foreground">Asesoramiento personalizado</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
